/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DigitizePointService } from '../../../map/command/digitize-point/digitize-point.service';
import { ReportDefinition, ReportSubtypeValue, ReportTypeValue } from '../../state/report.model';
import { filter, map, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators';
import { ClearReports, EnableTableEdit, SelectReportByName } from '../../state/report.actions';
import { Select, Store } from '@ngxs/store';
import { LoadDataForm } from '../../state/dataForm.actions';
import { MatDialog } from '@angular/material/dialog';
import { DataFormDialogComponent } from '../../dataForm/data-form-dialog.component';
import { BehaviorSubject, combineLatest, iif, Observable, of, Subject } from 'rxjs';
import { LoggerService } from '../../../shared/services/logger.service';
import { ReportState } from '../../state/report.state';
import { IsolatedReportService } from '../../services/isolatedReport.service';
import { ReportService } from '../../services/report.service';
import { PageState } from '../../../../core/state/page/page.state';
import { GsptService } from '../../../../components/gspt/gspt.service';
import { LineIsolationService } from '../../../../components/isolation/line-isolation.service';
import { isEqual } from 'lodash-es';

@Component({
    selector: 'dash-gis-report-controls',
    templateUrl: './report-controls.component.html',
    styleUrls: ['./report-controls.component.scss'],
})
export class ReportControlsComponent implements OnInit, OnChanges, OnDestroy {
    @Input() public definition: ReportDefinition;
    @Input() public keys: any[];
    @Input() public digitizeKey: string;

    // template observables
    public recordCount$: Observable<number>;
    public canZoom$: Observable<boolean>;
    public canTableEdit$: Observable<boolean>;
    public canEdit$: Observable<boolean>;
    public canMovePoint$: Observable<boolean>;
    public canDeletePoint$: Observable<boolean>;
    public shouldDisplay$: Observable<boolean>;

    @Select(PageState.mapEnabled) mapEnabled$: Observable<boolean>;

    public hasDefinition$ = new BehaviorSubject<boolean>(false);

    private mapName$ = new BehaviorSubject<string>(null);
    private layerName$ = new BehaviorSubject<string>(null);

    private destroy$ = new Subject<void>();

    constructor(
        private logger: LoggerService,
        private reportService: ReportService,
        private isolatedReportService: IsolatedReportService,
        private digitizeService: DigitizePointService,
        private dialog: MatDialog,
        private store: Store,
        private gspt: GsptService,
        private lineIsolation: LineIsolationService
    ) {}

    ngOnInit() {
        this.initObservables();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.definition) {
            const prev = changes.definition.previousValue;
            const current = changes.definition.currentValue;
            if (!isEqual(prev, current)) {
                this.hasDefinition$.next(!!current);
                this.mapName$.next(current?.map);
                this.layerName$.next(current?.layer);
            }
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
    }

    public movePoint() {
        this.store.dispatch(new ClearReports({ type: this.reportType }));
        this.digitizeService.movePoint(this.layer, this.digitizeKey);
    }

    public deletePoint() {
        this.store.dispatch(new ClearReports({ type: this.reportType }));
        this.digitizeService.deletePoint(this.layer, this.digitizeKey);
    }

    public getDataFormKey$() {
        return this.isolatedReportService.dataSource.dataFormKeys$.pipe(filter((d) => d.length >= 1));
    }

    public launchDataForm() {
        this.getDataFormKey$()
            .pipe(
                take(1),
                switchMap((keys) =>
                    this.store.dispatch(
                        new LoadDataForm({
                            name: this.definition.dataFormName,
                            keys,
                            initialSelection: null,
                            preLaunchCallback: null,
                            postSaveCallback: null,
                        })
                    )
                ),
                switchMap(() =>
                    this.dialog.open(DataFormDialogComponent, { data: this.definition.dataFormName }).afterClosed()
                )
            )
            .subscribe((dataSaved: boolean) => {
                if (dataSaved) {
                    this.isolatedReportService.triggerReport();
                }
            });
    }

    public enableTableEdit() {
        this.store.dispatch(new EnableTableEdit({ id: this.id, reportName: this.definition.name }));
    }

    public setOtherReport(report: ReportDefinition) {
        this.store.dispatch(new SelectReportByName({ reportName: report.name }));
    }

    public downloadExcel() {
        this.isolatedReportService.triggerExcelDownload();
    }

    private initObservables() {
        this.recordCount$ =
            (this.isolatedReportService.dataSource && this.isolatedReportService.dataSource.count$) || of(0);

        this.canZoom$ = combineLatest([this.recordCount$, this.hasData$]).pipe(
            map(([recordCount, hasData]) => this.definition?.enableZoom && hasData && recordCount === 1),
            takeUntil(this.destroy$)
        );

        this.canTableEdit$ = combineLatest([this.hasData$, this.isVolt$]).pipe(
            map(([hasData, isVolt]) => !!this.definition?.tableDataFormName && !isVolt && hasData),
            takeUntil(this.destroy$)
        );

        this.canEdit$ = this.hasData$.pipe(
            map((hasData) => !!this.definition?.dataFormName && hasData),
            takeUntil(this.destroy$)
        );

        this.canMovePoint$ = this.hasDefinition$.pipe(
            mergeMap((hasDef) =>
                iif(
                    () => hasDef,
                    this.digitizeService
                        .canMovePoint$(this.definition?.layer)
                        .pipe(map((canMove) => canMove && this.reportSubType === 'detail')),
                    of(false)
                )
            )
        );

        this.canDeletePoint$ = this.hasDefinition$.pipe(
            mergeMap((hasDef) =>
                iif(
                    () => hasDef,
                    this.digitizeService
                        .canDeletePoint$(this.definition?.layer)
                        .pipe(map((canDelete) => canDelete && this.reportSubType === 'detail')),
                    of(false)
                )
            )
        );

        const isLineIsolationEnabled$ = this.hasDefinition$.pipe(
            filter((d) => !!d),
            map(() => [this.definition?.map, this.definition?.layer]),
            mergeMap(([mapName, layer]) => this.lineIsolation.isIsolationEnabled(mapName, layer)),
            takeUntil(this.destroy$)
        );
        const isGsptEnabled$ = this.hasDefinition$.pipe(
            filter((d) => !!d),
            mergeMap(() => this.gspt.isGsptEnabled$(this.mapName$, this.layerName$)),
            takeUntil(this.destroy$)
        );

        this.shouldDisplay$ = combineLatest([
            this.hasDefinition$,
            this.canZoom$,
            this.canMovePoint$,
            this.canDeletePoint$,
            this.canTableEdit$,
            this.canEdit$,
            this.mapEnabled$,
            isGsptEnabled$,
            isLineIsolationEnabled$,
        ]).pipe(
            map(
                ([
                    hasDefinition,
                    canZoom,
                    canMovePoint,
                    canDeletePoint,
                    canTableEdit,
                    canEdit,
                    mapEnabled,
                    isGsptEnabled,
                    isLineIsolationEnabled,
                ]) =>
                    hasDefinition &&
                    (canZoom ||
                        canMovePoint ||
                        canDeletePoint ||
                        canTableEdit ||
                        canEdit ||
                        (mapEnabled && isLineIsolationEnabled && isGsptEnabled) ||
                        !this.definition.disablePrint ||
                        this.definition.excelEnabled ||
                        this.definition.otherReports.length > 0)
            ),
            takeUntil(this.destroy$)
        );
    }

    get id() {
        return this.isolatedReportService.id;
    }

    get otherReports() {
        const otherReports = [];
        if (this.definition && this.definition.otherReports) {
            const findDefFn = this.store.selectSnapshot(ReportState.reportByName);
            for (const other of this.definition.otherReports) {
                const def = findDefFn(other);
                if (def) {
                    otherReports.push(def);
                }
            }
        }
        return otherReports;
    }

    get excelEnabled(): boolean {
        return this.definition.excelEnabled;
    }

    get hasData$(): Observable<boolean> {
        return this.isolatedReportService.hasData$;
    }

    get isVolt$(): Observable<boolean> {
        return this.isolatedReportService.isVolt$;
    }

    get layer() {
        let layer = null;
        if (this.definition) {
            layer = this.definition.layer;
        }
        return layer;
    }

    get objectKey() {
        if (this.keys && this.keys.length === 1) {
            return this.keys[0];
        } else if (this.definition.type === 'data' && this.definition.subType === 'detail') {
            return this.isolatedReportService.objectKey;
        }
    }

    get reportType(): ReportTypeValue {
        return this.definition.type;
    }

    get reportSubType(): ReportSubtypeValue {
        return this.definition.subType;
    }
}
